import { useState, useCallback } from 'react';

/**
 * Enhanced table export hook with multiple format support
 * Supports CSV (client-side), Excel and PDF (requires additional libraries)
 */
export const useTableExport = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  /**
   * Format data for export
   */
  const formatDataForExport = useCallback((data, columns) => {
    return data.map(row => {
      const formattedRow = {};
      columns.forEach(col => {
        let value = '';

        const selector = col.selector || col.id || col.dataIndex;

        if (typeof selector === 'function') {
          value = selector(row);
        } else if (typeof selector === 'string') {
          value = row[selector];
        } else if (col.cell && typeof col.cell === 'function') {
          // If cell is a function, try to use it as a fallback
          value = col.cell(row);
        }

        // Apply custom formatting if available
        if (col.format && typeof col.format === 'function') {
          value = col.format(value, row);
        }

        // If value is a React element or object, try to get a string fallback from row
        if (value !== null && typeof value === 'object') {
          const fallbackKey = typeof col.selector === 'string' ? col.selector : (typeof col.id === 'string' ? col.id : null);
          if (fallbackKey && typeof row[fallbackKey] !== 'object') {
            value = row[fallbackKey];
          } else {
            value = ''; // Don't export [object Object]
          }
        }

        // Clean value for export (remove HTML tags, etc.)
        if (typeof value === 'string') {
          value = value.replace(/<[^>]*>/g, ''); // Remove HTML tags
        }

        formattedRow[col.name] = value || '';
      });
      return formattedRow;
    });
  }, []);

  /**
   * Enhanced CSV export with better formatting
   */
  const exportToCSV = useCallback(async (data, columns, filename = 'export') => {
    setExportLoading(true);
    setExportProgress(0);

    try {
      const formattedData = formatDataForExport(data, columns);
      setExportProgress(50);

      // Create CSV content
      const headers = columns.map(col => col.name);
      const csvContent = [
        headers.join(','),
        ...formattedData.map(row =>
          headers.map(header => {
            const value = row[header] || '';
            // Escape quotes and wrap in quotes if contains comma
            return typeof value === 'string' && (value.includes(',') || value.includes('"'))
              ? `"${value.replace(/"/g, '""')}"`
              : value;
          }).join(',')
        )
      ].join('\n');

      setExportProgress(80);

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      setExportProgress(100);
      return { success: true };
    } catch (error) {
      console.error('CSV Export Error:', error);
      return { success: false, error: error.message };
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  }, [formatDataForExport]);

  /**
   * Excel export via server API or XLSX library (if installed)
   */
  const exportToExcel = useCallback(async (data, columns, filename = 'export', options = {}) => {
    setExportLoading(true);
    setExportProgress(0);

    try {
      // Check if XLSX library is available for client-side export
      if (typeof window !== 'undefined' && window.XLSX) {
        const formattedData = formatDataForExport(data, columns);
        setExportProgress(30);

        const XLSX = window.XLSX;
        const wb = XLSX.utils.book_new();
        
        const wsData = [
          columns.map(col => col.name),
          ...formattedData.map(row => columns.map(col => row[col.name] || ''))
        ];

        setExportProgress(60);
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        
        // Auto-fit columns
        const colWidths = columns.map(col => {
          const maxLength = Math.max(
            col.name.length,
            ...formattedData.map(row => String(row[col.name] || '').length)
          );
          return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
        });
        ws['!cols'] = colWidths;

        setExportProgress(80);
        XLSX.utils.book_append_sheet(wb, ws, options.sheetName || 'Data');
        XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
        setExportProgress(100);
        
        return { success: true };
      } else {
        // Fallback to server-side export
        return await exportToExcelServer(data, columns, filename, options);
      }
    } catch (error) {
      console.error('Excel Export Error:', error);
      return { success: false, error: error.message };
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  }, [formatDataForExport]);

  /**
   * Server-side Excel export
   */
  const exportToExcelServer = useCallback(async (data, columns, filename = 'export', options = {}) => {
    const { apiEndpoint = '/api/export/excel' } = options;
    
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({
        data: formatDataForExport(data, columns),
        columns: columns.map(col => ({ name: col.name, key: col.id || col.name })),
        filename,
      }),
    });

    if (!response.ok) {
      throw new Error('Excel export failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${filename}_${Date.now()}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  }, [formatDataForExport]);

  /**
   * PDF export functionality
   */
  const exportToPDF = useCallback(async (data, columns, filename = 'export', options = {}) => {
    setExportLoading(true);
    setExportProgress(0);

    try {
      // Check if jsPDF is available for client-side export
      if (typeof window !== 'undefined' && window.jsPDF) {
        const formattedData = formatDataForExport(data, columns);
        setExportProgress(30);

        const { jsPDF } = window;
        const doc = new jsPDF({
          orientation: options.orientation || 'landscape',
          unit: 'mm',
          format: options.format || 'a4'
        });

        // Add title if provided
        if (options.title) {
          doc.setFontSize(16);
          doc.text(options.title, 14, 20);
        }

        setExportProgress(60);

        // Simple table generation (basic implementation)
        const tableData = formattedData.map(row =>
          columns.map(col => String(row[col.name] || ''))
        );

        // Note: This is a basic implementation. For advanced table features,
        // jsPDF autoTable plugin is recommended
        let y = options.title ? 30 : 20;
        const lineHeight = 5;
        
        // Headers
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        columns.forEach((col, i) => {
          doc.text(col.name, 14 + i * 30, y);
        });
        
        y += lineHeight;
        doc.setFont(undefined, 'normal');
        
        // Data rows
        tableData.forEach(row => {
          row.forEach((cell, i) => {
            doc.text(String(cell).substring(0, 20), 14 + i * 30, y);
          });
          y += lineHeight;
        });

        setExportProgress(80);
        doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
        setExportProgress(100);
        
        return { success: true };
      } else {
        // Fallback to server-side export or print
        return await exportToPDFServer(data, columns, filename, options);
      }
    } catch (error) {
      console.error('PDF Export Error:', error);
      return { success: false, error: error.message };
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  }, [formatDataForExport]);

  /**
   * Server-side PDF export fallback
   */
  const exportToPDFServer = useCallback(async (data, columns, filename = 'export', options = {}) => {
    const { apiEndpoint = '/api/export/pdf' } = options;
    
    if (apiEndpoint) {
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          data: formatDataForExport(data, columns),
          columns: columns.map(col => ({ name: col.name, key: col.id || col.name })),
          filename,
          options,
        }),
      });

      if (!response.ok) {
        throw new Error('PDF export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } else {
      // Browser print fallback
      window.print();
    }

    return { success: true };
  }, [formatDataForExport]);

  /**
   * Main export function that handles all formats
   */
  const exportData = useCallback(async (format, data, columns, filename = 'export', options = {}) => {
    switch (format.toLowerCase()) {
      case 'csv':
        return await exportToCSV(data, columns, filename);
      case 'excel':
      case 'xlsx':
        return await exportToExcel(data, columns, filename, options);
      case 'pdf':
        return await exportToPDF(data, columns, filename, options);
      default:
        return { success: false, error: `Unsupported format: ${format}` };
    }
  }, [exportToCSV, exportToExcel, exportToPDF]);

  /**
   * Export selected rows only
   */
  const exportSelected = useCallback(async (format, selectedRows, columns, filename = 'export_selected', options = {}) => {
    if (!selectedRows || selectedRows.length === 0) {
      return { success: false, error: 'No rows selected for export' };
    }
    
    return await exportData(format, selectedRows, columns, filename, options);
  }, [exportData]);

  /**
   * Get export file name with timestamp - Legacy compatibility
   */
  const getExportFileName = useCallback((format) => {
    return `export_${new Date().toISOString().split('T')[0]}.${format}`;
  }, []);

  /**
   * Prepare CSV data - Legacy compatibility
   */
  const prepareCSVData = useCallback((exportData, columns) => {
    const formattedData = formatDataForExport(exportData, columns);
    const headers = columns.map(col => ({ label: col.name, key: col.id || col.name }));
    return { data: formattedData, headers };
  }, [formatDataForExport]);

  return {
    // New enhanced methods
    exportLoading,
    exportProgress,
    exportData,
    exportSelected,
    exportToCSV,
    exportToExcel,
    exportToPDF,
    
    // Legacy compatibility
    loading: exportLoading,
    getExportFileName,
    prepareCSVData,
  };
};
